import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { on } from "@ember/modifier";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { fn, concat, hash } from "@ember/helper";
import { htmlSafe } from "@ember/template";
import eq from "truth-helpers/helpers/eq";
import gt from "truth-helpers/helpers/gt";
import not from "truth-helpers/helpers/not";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import PluginOutlet from "discourse/components/plugin-outlet";
import ProxyviewsSvgChart from "./proxyviews-svg-chart";
import icon from "discourse-common/helpers/d-icon";

const formatNumber = (num) => {
  if (num === null || num === undefined || num === "") {
    return "—";
  }
  return Number(num).toLocaleString();
};

const formatPercent = (num) => {
  if (num === null || num === undefined || num === "") {
    return "—";
  }
  return `${num}%`;
};

const formatScore = (num, total = 100) => {
  if (num === null || num === undefined || num === "") {
    return "—";
  }
  return `${num}/${total}`;
};

const isTrueVal = (val) => {
  return val === true || val === "true" || val === "Yes" || val === "available";
};

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "specs", label: "Specs" },
  { id: "setup", label: "Setup" },
];

const getScore = (distribution, score) => {
  return distribution?.[score] || 0;
};

const barWidth = (count, total) => {
  if (!total) {
    return "0%";
  }
  return `${Math.round((count / total) * 100)}%`;
};

export default class ProxyviewsProviderDetail extends Component {
  @service currentUser;
  @service dialog;
  @service router;
  @tracked loading = false;
  @tracked reviewScore = null;
  @tracked reviewWorks = "";
  @tracked reviewHurts = "";
  @tracked claiming = false;
  @tracked claimEmail = "";
  @tracked claimNotes = "";
  @tracked showClaimForm = false;
@tracked historicalBenchmarks = [];
@tracked benchmarksLoading = true;
@tracked benchmarksError = false;

  @action
  setProperty(property, event) {
    this[property] = event.target.value;
  }

constructor() {
  super(...arguments);
  this.fetchBenchmarks();
}

  async fetchBenchmarks() {
    try {
      const data = await ajax(`/benchmarks/providers/${this.provider?.slug}.json`);
      if (data && data.benchmark_runs) {
        // Each run is wrapped in { benchmark_run: {...} } — flatten it
        this.historicalBenchmarks = data.benchmark_runs.map(r => r.benchmark_run || r);
      }
    } catch(e) {
      // 404 = benchmarks plugin not loaded or no data — not an error, just empty
      if (e?.status !== 404) {
        this.benchmarksError = true;
      }
    } finally {
      this.benchmarksLoading = false;
    }
  }


  get latestBenchmark() {
    return this.args.model?.latest_benchmark || this.historicalBenchmarks?.[0] || null;
  }

  get isOwner() {
    return this.currentUser && this.provider?.owner_id === this.currentUser.id;
  }

  get hasIpPricing() {
    // Check the provider's PRIMARY type (first in proxy_types), not all types.
    // A mixed provider like IPRoyal (residential + datacenter) should show /GB
    // because residential is the primary type, even though datacenter is /IP.
    const tp = this.provider?.type_pricing;
    if (!tp) return false;
    const primaryType = this.provider?.proxy_types?.[0];
    if (primaryType && tp[primaryType]) {
      return tp[primaryType].unit === "per_ip";
    }
    // Fallback: only per_ip if ALL types are per_ip (pure datacenter/ISP providers)
    const allUnits = Object.values(tp).map((d) => d.unit);
    return allUnits.length > 0 && allUnits.every((u) => u === "per_ip");
  }

  @action
  showClaimModal() {
    if (!this.currentUser) {
      this.dialog.alert("You must be logged in to claim a listing.");
      return;
    }
    this.showClaimForm = true;
  }

  @action
  closeClaimModal() {
    this.showClaimForm = false;
    this.claimEmail = "";
    this.claimNotes = "";
  }

  @action
  stopPropagation(e) {
    e.stopPropagation();
  }

  @action
  async submitClaim() {
    if (!this.claimEmail) {
      this.dialog.alert("Please enter your company email.");
      return;
    }
    
    this.claiming = true;
    try {
      await ajax(`/providers/${this.provider?.slug}/claims`, {
        type: "POST",
        data: {
          company_email: this.claimEmail,
          notes: this.claimNotes
        }
      });
      this.dialog.alert("Claim submitted successfully! Our team will review it shortly.");
      this.closeClaimModal();
    } catch (e) {
      this.dialog.alert(e.jqXHR?.responseJSON?.errors?.[0] || "Failed to submit claim.");
    } finally {
      this.claiming = false;
    }
  }

  @tracked tab = "overview";
  @tracked showReviewModal = false;
  @tracked submitting = false;
  @tracked submitError = null;
  @tracked showSuccessMessage = false;

  @tracked reviewTitle = "";
  @tracked reviewOverall = 5;
  @tracked reviewReliability = 5;
  @tracked reviewSpeed = 5;
  @tracked reviewSupport = 5;
  @tracked reviewValue = 5;
  @tracked reviewWorks = "";
  @tracked reviewHurts = "";

  get tabs() {
    const list = [...TABS];
    if (this.args.model.changelog && this.args.model.changelog.length > 0) {
      list.push({ id: "changelog", label: "History" });
    }
    return list;
  }

  get provider() {
    return this.args.model.provider;
  }
  get profile() {
    return this.args.model.profile;
  }

  @action setTab(id) {
    this.tab = id;
  }

  @action handleLogoError(e) {
    console.log("handleLogoError triggered for", e.target.src);
    e.target.style.display = "none";
    const fallback = e.target.parentElement.querySelector('[class*="logo-placeholder"]');
    if (fallback) {
      fallback.style.display = "flex";
    }
  }

  @action checkLogoLoad(element) {
    if (element.complete && element.naturalWidth === 0) {
      console.log("checkLogoLoad found broken image:", element.src);
      this.handleLogoError({ target: element });
    }
  }

  formatDate(dateStr) {
    if (!dateStr) {
      return "";
    }
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  }

  @action openReviewModal() {
    if (!this.currentUser) {
      document.querySelector(".login-button")?.click();
      return;
    }
    this.showReviewModal = true;
  }

  @action closeReviewModal() {
    this.showReviewModal = false;
    this.submitError = null;
    this.reviewTitle = "";
    this.reviewOverall = 5;
    this.reviewReliability = 5;
    this.reviewSpeed = 5;
    this.reviewSupport = 5;
    this.reviewValue = 5;
    this.reviewWorks = "";
    this.reviewHurts = "";
  }

  @action stopProp(e) {
    e.stopPropagation();
  }

  @action updateTitle(e) { this.reviewTitle = e.target.value; }
  @action updateOverall(e) { this.reviewOverall = parseInt(e.target.value, 10); }
  @action updateReliability(e) { this.reviewReliability = parseInt(e.target.value, 10); }
  @action updateSpeed(e) { this.reviewSpeed = parseInt(e.target.value, 10); }
  @action updateSupport(e) { this.reviewSupport = parseInt(e.target.value, 10); }
  @action updateValue(e) { this.reviewValue = parseInt(e.target.value, 10); }
  @action updateWorks(e) { this.reviewWorks = e.target.value; }
  @action updateHurts(e) { this.reviewHurts = e.target.value; }

  @action async submitReview(e) {
    e.preventDefault();
    this.submitting = true;
    this.submitError = null;
    
    try {
      const payload = {
        review: {
          provider_id: this.provider.id,
          title: this.reviewTitle,
          overall_score: this.reviewOverall,
          reliability_score: this.reviewReliability,
          speed_score: this.reviewSpeed,
          support_score: this.reviewSupport,
          value_score: this.reviewValue,
          what_works: this.reviewWorks,
          what_hurts: this.reviewHurts
        }
      };

      const result = await ajax("/reviews.json", {
        type: "POST",
        data: payload
      });

      if (result && (result.review || result.id)) {
        this.showSuccessMessage = true;
        this.showReviewModal = false;
        // Refresh route model to show the new review without a full page reload
        setTimeout(() => { this.showSuccessMessage = false; this.router.refresh(); }, 2000);
      } else {
        this.submitError = "Failed to submit review. Please try again.";
      }
    } catch (err) {
      this.submitError = err.responseJSON?.errors?.join(", ") || "Failed to submit review. Check your inputs.";
    } finally {
      this.submitting = false;
    }
  }

  <template>
    <section class="pd-detail">
      <div class="pd-detail__hero">
        <div class="pd-detail__hero-main">
          <div class="pd-detail__logo">
            {{#if this.provider.logo}}
              <img src={{this.provider.logo}} alt={{this.provider.name}} class="pd-detail__logo-img" fetchpriority="high" {{on "error" this.handleLogoError}} {{didInsert this.checkLogoLoad}} />
              <span class="pd-detail__logo-placeholder" style="display: none;">
                {{this.provider.logoInitial}}
              </span>
            {{else}}
              <span class="pd-detail__logo-placeholder">{{this.provider.logoInitial}}</span>
            {{/if}}
          </div>
          <div class="pd-detail__heading">
            <h1 class="pd-detail__name">
              {{this.provider.name}}
              {{#if this.provider.tierLabel}}
                <span class="pd-badge {{this.provider.tierClass}}">{{this.provider.tierLabel}}</span>
              {{/if}}
            </h1>
            <div class="pd-detail__rating">
              <span class="pd-detail__stars">★</span>
              <strong>{{this.provider.ratingDisplay}}</strong>
              <span class="pd-detail__rating-count">({{this.provider.review_count}} reviews)</span>
            </div>
            {{#if this.provider.proxy_types}}
              <div class="pd-detail__types">
                {{#each this.provider.proxy_types as |t|}}
                  <span class="pd-type-pill pd-type-{{t}}">{{t}}</span>
                {{/each}}
              </div>
            {{/if}}
            <p class="pd-detail__desc">{{this.provider.description}}</p>
          </div>
        </div>
        <div class="pd-detail__hero-actions">
          <a class="pd-btn pd-btn--hero" href={{this.provider.goUrl}} rel="sponsored nofollow" target="_blank">
            Visit Website <span class="arrow">↗</span>
          </a>
          {{#unless this.provider.owner_id}}
            <button type="button" class="pd-btn pd-btn--claim" {{on "click" this.showClaimModal}}>
              <span class="pd-btn__icon" aria-hidden="true">{{icon "shield-alt"}}</span>
              <span>Claim this Listing</span>
            </button>
          {{/unless}}
          {{#if this.isOwner}}
            <a class="pd-btn pd-btn--secondary" href="/vendor/proxyviews-directory">
              Vendor Dashboard
            </a>
          {{/if}}
        </div>
      </div>

      <div class="pd-detail__quickstats">
        <div class="pd-detail__quickstat">
          <span class="pd-detail__quickstat-value">{{this.provider.costDisplay}}</span>
          <span class="pd-detail__quickstat-label">{{#if this.hasIpPricing}}per IP{{else}}per GB{{/if}}</span>
        </div>
        <div class="pd-detail__quickstat">
          <span class="pd-detail__quickstat-value">{{formatNumber this.provider.ip_pool_size}}</span>
          <span class="pd-detail__quickstat-label">IP Pool</span>
        </div>
        <div class="pd-detail__quickstat">
          <span class="pd-detail__quickstat-value">{{formatPercent this.provider.uptime_percentage}}</span>
          <span class="pd-detail__quickstat-label">Uptime</span>
        </div>
        <div class="pd-detail__quickstat">
          <span class="pd-detail__quickstat-value">{{if this.provider.benchmark_score this.provider.benchmark_score "—"}}</span>
          <span class="pd-detail__quickstat-label">Benchmark</span>
        </div>
        {{#if @model.latest_benchmark}}
          <div class="pd-detail__quickstat">
            <span class="pd-detail__quickstat-value">{{@model.latest_benchmark.avg_response_time_ms}}ms</span>
            <span class="pd-detail__quickstat-label">Avg Response</span>
          </div>
        {{/if}}
        {{#if this.provider.metadata.free_trial}}
          <div class="pd-detail__quickstat pd-detail__quickstat--highlight">
            <span class="pd-detail__quickstat-value">{{this.provider.metadata.free_trial}}</span>
            <span class="pd-detail__quickstat-label">Free Trial</span>
          </div>
        {{/if}}
      </div>

      <div class="pd-detail__layout">
        <div class="pd-detail__main">
            <div class="pd-tabs">
              {{#each this.tabs as |t|}}
              <button
                type="button"
                class="pd-tabs__tab {{if (eq this.tab t.id) 'pd-tabs__tab--active'}}"
                {{on "click" (fn this.setTab t.id)}}
              >{{t.label}}</button>
            {{/each}}
          </div>

          {{#if (eq this.tab "overview")}}
            {{#if this.profile.overview}}
              <div class="pd-detail__card">
                <h3>Overview</h3>
                <p>{{this.profile.overview}}</p>
              </div>
            {{/if}}
            <div class="pd-detail__proscons">
              <div class="pd-detail__card pd-detail__card--pros">
                <div class="pd-proscons-header">
                  <span class="pd-proscons-icon">👍</span>
                  <h3>Pros</h3>
                </div>
                <ul class="pd-list-pros">
                  {{#each this.profile.pros as |item|}}
                    <li>
                      <span class="pd-icon pd-icon--pro">✓</span>
                      <span class="pd-proscons-text">{{item}}</span>
                    </li>
                  {{else}}
                    <li class="pd-empty-state">
                      <span class="pd-empty-state__icon">✓</span>
                      <span class="pd-empty-state__text">No pros listed yet</span>
                    </li>
                  {{/each}}
                </ul>
              </div>
              <div class="pd-detail__card pd-detail__card--cons">
                <div class="pd-proscons-header pd-proscons-header--cons">
                  <span class="pd-proscons-icon">👎</span>
                  <h3>Cons</h3>
                </div>
                <ul class="pd-list-cons">
                  {{#each this.profile.cons as |item|}}
                    <li>
                      <span class="pd-icon pd-icon--con">✕</span>
                      <span class="pd-proscons-text">{{item}}</span>
                    </li>
                  {{else}}
                    <li class="pd-empty-state">
                      <span class="pd-empty-state__icon">—</span>
                      <span class="pd-empty-state__text">No cons listed yet</span>
                    </li>
                  {{/each}}
                </ul>
              </div>
            </div>
            {{#if this.profile.what_works}}
              <div class="pd-detail__card">
                <h3>What works well</h3>
                <p>{{this.profile.what_works}}</p>
              </div>
            {{/if}}
            {{#if this.profile.what_hurts}}
              <div class="pd-detail__card">
                <h3>What could be better</h3>
                <p>{{this.profile.what_hurts}}</p>
              </div>
            {{/if}}
            {{#if this.profile.faq.length}}
              <div class="pd-detail__card">
                <h3>FAQ</h3>
                {{#each this.profile.faq as |qa|}}
                  <details class="pd-faq">
                    <summary>{{qa.q}}</summary>
                    <p>{{qa.a}}</p>
                  </details>
                {{/each}}
              </div>
            {{/if}}

            <div class="pd-detail__card pd-detail__card--benchmarks">
              <h3>Performance Benchmarks Summary</h3>
              <div class="pd-detail__progress-bars">
                <div class="pd-progress-item">
                  <div class="pd-progress-header">
                    <span class="label">Uptime Guarantee</span>
                    <span class="value">{{formatPercent this.provider.uptime_percentage}}</span>
                  </div>
                  <div class="pd-progress-track">
                    <div class="pd-progress-fill pd-progress-fill--green" style={{htmlSafe (concat "width: " (if this.provider.uptime_percentage this.provider.uptime_percentage 0) "%")}}></div>
                  </div>
                </div>
                <div class="pd-progress-item">
                  <div class="pd-progress-header">
                    <span class="label">Block Rate (Tested)</span>
                    <span class="value">{{formatPercent this.provider.verified_block_rate}}</span>
                  </div>
                  <div class="pd-progress-track">
                    <div class="pd-progress-fill pd-progress-fill--red" style={{htmlSafe (concat "width: " (if this.provider.verified_block_rate this.provider.verified_block_rate 0) "%")}}></div>
                  </div>
                </div>
                <div class="pd-progress-item">
                  <div class="pd-progress-header">
                    <span class="label">ASN Diversity Score</span>
                    <span class="value">{{formatScore this.provider.asn_diversity_score 100}}</span>
                  </div>
                  <div class="pd-progress-track">
                    <div class="pd-progress-fill pd-progress-fill--blue" style={{htmlSafe (concat "width: " (if this.provider.asn_diversity_score this.provider.asn_diversity_score 0) "%")}}></div>
                  </div>
                </div>
              </div>
            </div>

            {{#if this.latestBenchmark}}
              <div class="pd-detail__card pd-detail__card--benchmark-details">
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;">
                  <h3 style="margin: 0;">⚡ Latest Benchmark Run Details</h3>
                  {{#if this.latestBenchmark.performance_grade}}
                    <span class="badge badge-type pd-grade-badge pd-grade-badge--{{this.latestBenchmark.performance_grade}}">
                      Grade: {{this.latestBenchmark.performance_grade}}
                    </span>
                  {{/if}}
                </div>
                <p class="pd-detail__benchmark-intro" style="margin-bottom: 16px;">
                  Tested on <strong>{{this.formatDate this.latestBenchmark.tested_at}}</strong>
                  {{#if this.latestBenchmark.test_type}}
                    &bull; Type: <code>{{this.latestBenchmark.test_type}}</code>
                  {{/if}}
                  {{#if this.latestBenchmark.region}}
                    &bull; Region: <code>{{this.latestBenchmark.region}}</code>
                  {{/if}}
                </p>
                <div class="pd-detail__benchmark-grid">
                  <div class="pd-benchmark-metric">
                    <span class="label">Avg Response Time</span>
                    <span class="value">{{formatNumber this.latestBenchmark.avg_response_time_ms}}ms</span>
                  </div>
                  <div class="pd-benchmark-metric">
                    <span class="label">p95 Response Time</span>
                    <span class="value">{{formatNumber this.latestBenchmark.p95_response_time_ms}}ms</span>
                  </div>
                  <div class="pd-benchmark-metric">
                    <span class="label">Success Rate</span>
                    <span class="value value--success">{{formatPercent this.latestBenchmark.success_rate}}</span>
                  </div>
                  <div class="pd-benchmark-metric">
                    <span class="label">Block Rate</span>
                    <span class="value {{if (gt this.latestBenchmark.block_rate 5) 'value--danger' 'value--neutral'}}">{{formatPercent this.latestBenchmark.block_rate}}</span>
                  </div>
                </div>
              </div>
            {{/if}}

            <div class="pd-detail__card pd-detail__card--benchmarks" style="margin-top: 24px;">
              <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;">
                <h3 class="pd-detail__card-title" style="margin: 0;">Historical Benchmark Speeds</h3>
                {{#if this.historicalBenchmarks.length}}
                  <span class="pd-detail__benchmark-count" style="font-size: 0.85rem; color: var(--primary-medium);">
                    Showing {{this.historicalBenchmarks.length}} runs
                  </span>
                {{/if}}
              </div>
              {{#if this.benchmarksLoading}}
                <div style="min-height: 200px; display: flex; align-items: center; justify-content: center; color: var(--primary-medium);">
                  <span class="spinner small" style="margin-right: 8px;"></span> Loading benchmark data…
                </div>
              {{else if this.benchmarksError}}
                <p class="pd-detail__benchmark-intro" style="color: var(--danger);">Couldn't load benchmark history. Try refreshing the page.</p>
              {{else if (not this.historicalBenchmarks.length)}}
                <div style="min-height: 150px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--primary-medium); text-align: center; padding: 24px;">
                  <span style="font-size: 2rem; margin-bottom: 8px; opacity: 0.6;">📊</span>
                  <p style="margin: 0; font-weight: 500;">No historical benchmark speed tests recorded yet for this provider.</p>
                  <span style="font-size: 0.85rem; margin-top: 4px; opacity: 0.7;">Run automated benchmarks in Admin &gt; Plugins &gt; Benchmarks to collect response time history.</span>
                </div>
              {{else}}
                <p class="pd-detail__benchmark-intro" style="margin-bottom: 16px;">Response time trend (ms) over recent benchmark runs.</p>
                <ProxyviewsSvgChart @data={{this.historicalBenchmarks}} @metricKey="avg_response_time_ms" @width="800" @height="250" />
              {{/if}}
            </div>

            <PluginOutlet @name="proxyviews-provider-benchmark" @outletArgs={{hash provider=this.provider}} />

            <div class="pd-detail__card pd-detail__card--reviews">
              <h3>Community Consensus Summary</h3>
              <div class="pd-detail__reviews-summary">
                <div class="rating-box">
                  <span class="stars">★★★★★</span>
                  <strong class="score">{{this.provider.ratingDisplay}}</strong>
                  <span class="count">Based on {{this.provider.review_count}} user reviews</span>
                  <button type="button" class="btn btn-primary pd-write-review-btn" {{on "click" this.openReviewModal}}>
                    ✍️ Write a Review
                  </button>
                  {{#if this.showSuccessMessage}}
                    <div class="pd-review-success-banner alert alert-success">
                      🎉 Review submitted successfully!
                    </div>
                  {{/if}}
                </div>
                {{#if @model.review_summary.score_distribution}}
                  <div class="rating-bars">
                    <div class="rating-bar-row">
                      <span class="bar-label">5 ★</span>
                      <div class="bar-track">
                        <div class="bar-fill" style="width: {{barWidth (getScore @model.review_summary.score_distribution "5") @model.review_summary.total_reviews}}"></div>
                      </div>
                      <span class="bar-count">{{getScore @model.review_summary.score_distribution "5"}}</span>
                    </div>
                    <div class="rating-bar-row">
                      <span class="bar-label">4 ★</span>
                      <div class="bar-track">
                        <div class="bar-fill" style="width: {{barWidth (getScore @model.review_summary.score_distribution "4") @model.review_summary.total_reviews}}"></div>
                      </div>
                      <span class="bar-count">{{getScore @model.review_summary.score_distribution "4"}}</span>
                    </div>
                    <div class="rating-bar-row">
                      <span class="bar-label">3 ★</span>
                      <div class="bar-track">
                        <div class="bar-fill" style="width: {{barWidth (getScore @model.review_summary.score_distribution "3") @model.review_summary.total_reviews}}"></div>
                      </div>
                      <span class="bar-count">{{getScore @model.review_summary.score_distribution "3"}}</span>
                    </div>
                    <div class="rating-bar-row">
                      <span class="bar-label">2 ★</span>
                      <div class="bar-track">
                        <div class="bar-fill" style="width: {{barWidth (getScore @model.review_summary.score_distribution "2") @model.review_summary.total_reviews}}"></div>
                      </div>
                      <span class="bar-count">{{getScore @model.review_summary.score_distribution "2"}}</span>
                    </div>
                    <div class="rating-bar-row">
                      <span class="bar-label">1 ★</span>
                      <div class="bar-track">
                        <div class="bar-fill" style="width: {{barWidth (getScore @model.review_summary.score_distribution "1") @model.review_summary.total_reviews}}"></div>
                      </div>
                      <span class="bar-count">{{getScore @model.review_summary.score_distribution "1"}}</span>
                    </div>
                  </div>
                {{else}}
                  <div class="review-highlights">
                    <div class="highlight-item">
                      <strong>Community rating:</strong> Users rate this provider highly for speed, setup ease, and reliable connection stability.
                    </div>
                  </div>
                {{/if}}
              </div>
            </div>

            {{#if @model.recent_reviews.length}}
              <div class="pd-detail__card pd-detail__card--recent-reviews">
                <h3>💬 Recent User Reviews</h3>
                <div class="pd-reviews-list">
                  {{#each @model.recent_reviews as |rev|}}
                    <div class="pd-review-item">
                      <div class="pd-review-item__header">
                        <div class="pd-review-item__user">
                          <span class="pd-review-item__avatar">
                            {{rev.user.username}}
                          </span>
                          <span class="pd-review-item__date">{{this.formatDate rev.created_at}}</span>
                        </div>
                        <div class="pd-review-item__rating">
                          <span class="stars">★</span>
                          <strong>{{rev.overall_score}}/10</strong>
                        </div>
                      </div>
                      <h4 class="pd-review-item__title">{{rev.title}}</h4>
                      <div class="pd-review-item__body">
                        <div class="pd-review-item__works">
                          <strong>👍 What works:</strong> {{rev.what_works}}
                        </div>
                        <div class="pd-review-item__hurts">
                          <strong>👎 What hurts:</strong> {{rev.what_hurts}}
                        </div>
                      </div>
                      {{#if rev.response}}
                        <div class="pd-review-response">
                          <div class="pd-review-response__header">
                            <span class="pd-review-response__badge">🏪 Vendor Response</span>
                            <span class="pd-review-response__author">{{if rev.response.user.name rev.response.user.name rev.response.user.username}}</span>
                            <span class="pd-review-response__time">{{this.formatDate rev.response.created_at}}</span>
                          </div>
                          <div class="pd-review-response__body">
                            {{rev.response.body}}
                          </div>
                        </div>
                      {{/if}}
                    </div>
                  {{/each}}
                </div>
              </div>
            {{else}}
              <div class="pd-detail__card pd-detail__card--reviews-empty">
                <p class="pd-detail__empty-text">No reviews yet. Be the first to share your experience!</p>
              </div>
            {{/if}}

            <div class="pd-detail__card pd-detail__card--alternatives">
              <h3>Alternative Competitor Recommendations</h3>
              <p class="pd-detail__alt-sub">If {{this.provider.name}} isn't the right fit, consider these top-rated alternatives:</p>
              <div class="pd-detail__alternatives-grid">
                {{#each @model.alternatives as |alt|}}
                  <div class="pd-alt-card">
                    <div class="pd-alt-card__header">
                      <div class="pd-alt-card__logo">
                        {{#if alt.logo}}
                          <img src={{alt.logo}} alt={{alt.name}} class="pd-alt-card__logo-img" loading="lazy" {{on "error" this.handleLogoError}} {{didInsert this.checkLogoLoad}} />
                          <span class="pd-alt-card__logo-placeholder" style="display: none;">
                            {{alt.logoInitial}}
                          </span>
                        {{else}}
                          <span class="pd-alt-card__logo-placeholder">{{alt.logoInitial}}</span>
                        {{/if}}
                      </div>
                      <div class="pd-alt-card__content">
                        <h4 class="name">{{alt.name}}</h4>
                        <div class="meta">{{alt.display_proxy_type}} · ★ {{alt.ratingDisplay}}</div>
                      </div>
                    </div>
                    <a href={{alt.profileUrl}} class="pd-btn">View Profile →</a>
                  </div>
                {{else}}
                  <p class="pd-detail__no-alts">No top-rated alternatives found for this proxy type.</p>
                {{/each}}
              </div>
            </div>

            {{#if @model.related_guides.length}}
              <div class="pd-detail__card pd-detail__card--related-guides" style="margin-top: 24px;">
                <h3>📖 Related Expert Guides</h3>
                <p class="pd-detail__guide-sub">Read our in-depth analysis and buying advice related to {{this.provider.name}}.</p>
                <div class="pd-related-guides__list">
                  {{#each @model.related_guides as |guide|}}
                    <a href={{guide.url}} class="pd-related-guides__item">
                      <span class="pd-related-guides__title">{{guide.title}}</span>
                      <span class="pd-related-guides__meta">{{guide.category}}</span>
                    </a>
                  {{/each}}
                </div>
              </div>
            {{/if}}
          {{/if}}

          {{#if (eq this.tab "specs")}}
            <div class="pd-detail__card pd-specs-container">
              <div class="pd-specs-header">
                <h3>Full specifications</h3>
                <span class="pd-specs-subtitle">Detailed technical specifications, pricing models, and performance metrics for {{this.provider.name}}</span>
              </div>

              <!-- Group 1: Performance & Reliability -->
              <div class="pd-spec-group">
                <h4 class="pd-spec-group__title">
                  <span class="pd-spec-group__icon">⚡</span> Performance & Benchmark Metrics
                </h4>
                <div class="pd-spec-group__grid">
                  <div class="pd-spec-item pd-spec-item--bar">
                    <div class="pd-spec-item__header">
                      <span class="pd-spec-item__label">Uptime</span>
                      <strong class="pd-spec-item__value pd-spec-item__value--green">{{formatPercent this.provider.uptime_percentage}}</strong>
                    </div>
                    <div class="pd-progress-track pd-progress-track--mini">
                      <div class="pd-progress-fill pd-progress-fill--green" style={{htmlSafe (concat "width: " (if this.provider.uptime_percentage this.provider.uptime_percentage 0) "%")}}></div>
                    </div>
                  </div>

                  <div class="pd-spec-item pd-spec-item--bar">
                    <div class="pd-spec-item__header">
                      <span class="pd-spec-item__label">Block Rate</span>
                      <strong class="pd-spec-item__value">{{formatPercent this.provider.verified_block_rate}}</strong>
                    </div>
                    <div class="pd-progress-track pd-progress-track--mini">
                      <div class="pd-progress-fill pd-progress-fill--red" style={{htmlSafe (concat "width: " (if this.provider.verified_block_rate this.provider.verified_block_rate 0) "%")}}></div>
                    </div>
                  </div>

                  <div class="pd-spec-item pd-spec-item--bar">
                    <div class="pd-spec-item__header">
                      <span class="pd-spec-item__label">ASN Diversity</span>
                      <strong class="pd-spec-item__value">{{formatScore this.provider.asn_diversity_score 100}}</strong>
                    </div>
                    <div class="pd-progress-track pd-progress-track--mini">
                      <div class="pd-progress-fill pd-progress-fill--blue" style={{htmlSafe (concat "width: " (if this.provider.asn_diversity_score this.provider.asn_diversity_score 0) "%")}}></div>
                    </div>
                  </div>

                  <div class="pd-spec-item">
                    <span class="pd-spec-item__label">Benchmark Score</span>
                    <strong class="pd-spec-item__value">{{if this.provider.benchmark_score this.provider.benchmark_score "—"}}</strong>
                  </div>

                  <div class="pd-spec-item">
                    <span class="pd-spec-item__label">Session Affinity</span>
                    <strong class="pd-spec-item__value">{{if this.provider.avg_session_affinity_length (concat this.provider.avg_session_affinity_length " min") "—"}}</strong>
                  </div>

                  {{#if this.provider.metadata.speed}}
                    <div class="pd-spec-item">
                      <span class="pd-spec-item__label">Tested Speed</span>
                      <strong class="pd-spec-item__value">{{this.provider.metadata.speed}}</strong>
                    </div>
                  {{/if}}
                </div>
              </div>

              <!-- Group 2: Pricing & Terms -->
              <div class="pd-spec-group">
                <h4 class="pd-spec-group__title">
                  <span class="pd-spec-group__icon">💳</span> Pricing & Payment Terms
                </h4>
                <div class="pd-spec-group__grid">
                  <div class="pd-spec-item">
                    <span class="pd-spec-item__label">{{#if this.hasIpPricing}}Cost / IP{{else}}Cost / GB{{/if}}</span>
                    <strong class="pd-spec-item__value pd-spec-item__value--highlight">{{this.provider.costDisplay}}</strong>
                  </div>

                  <div class="pd-spec-item">
                    <span class="pd-spec-item__label">Cost Tier</span>
                    <strong class="pd-spec-item__value pd-spec-item__value--capitalize">{{if this.provider.cost_tier this.provider.cost_tier "—"}}</strong>
                  </div>

                  <div class="pd-spec-item">
                    <span class="pd-spec-item__label">Pay As You Go</span>
                    <div class="pd-spec-item__value">
                      {{#if (isTrueVal this.provider.metadata.pay_as_you_go)}}
                        <span class="pd-spec-badge pd-spec-badge--success">✓ Yes</span>
                      {{else if this.provider.metadata.pay_as_you_go}}
                        <span class="pd-spec-badge pd-spec-badge--neutral">{{this.provider.metadata.pay_as_you_go}}</span>
                      {{else}}
                        <span class="pd-spec-badge pd-spec-badge--disabled">No</span>
                      {{/if}}
                    </div>
                  </div>

                  {{#if this.provider.metadata.free_trial}}
                    <div class="pd-spec-item">
                      <span class="pd-spec-item__label">Free Trial</span>
                      <div class="pd-spec-item__value">
                        {{#if (isTrueVal this.provider.metadata.free_trial)}}
                          <span class="pd-spec-badge pd-spec-badge--success">✓ Available</span>
                        {{else}}
                          <span class="pd-spec-badge pd-spec-badge--info">{{this.provider.metadata.free_trial}}</span>
                        {{/if}}
                      </div>
                    </div>
                  {{/if}}

                  {{#if this.provider.type_pricing}}
                    <div class="pd-spec-item pd-spec-item--full">
                      <span class="pd-spec-item__label">Price by Type</span>
                      <div class="pd-type-price-breakdown">
                        {{#each-in this.provider.type_pricing as |type data|}}
                          <span class="pd-type-price-tag pd-type-{{type}}">
                            <span class="pd-type-price-tag__name">{{type}}</span>
                            <span class="pd-type-price-tag__price">${{data.price}}{{if (eq data.unit "per_ip") "/IP" (if (eq data.unit "flat") " flat" "/GB")}}</span>
                          </span>
                        {{/each-in}}
                      </div>
                    </div>
                  {{/if}}
                </div>
              </div>

              <!-- Group 3: Network & Architecture -->
              <div class="pd-spec-group">
                <h4 class="pd-spec-group__title">
                  <span class="pd-spec-group__icon">🌐</span> Network & Architecture
                </h4>
                <div class="pd-spec-group__grid">
                  <div class="pd-spec-item">
                    <span class="pd-spec-item__label">IP Pool Size</span>
                    <strong class="pd-spec-item__value">{{formatNumber this.provider.ip_pool_size}}</strong>
                  </div>

                  {{#if this.provider.metadata.locations}}
                    <div class="pd-spec-item">
                      <span class="pd-spec-item__label">Locations</span>
                      <strong class="pd-spec-item__value">{{this.provider.metadata.locations}}</strong>
                    </div>
                  {{/if}}
                </div>
              </div>

              {{#if this.provider.protocols.length}}
                <div class="pd-spec-chips-section">
                  <h4 class="pd-detail__subhead">Supported Protocols</h4>
                  <div class="pd-detail__chips">
                    {{#each this.provider.protocols as |pr|}}
                      <span class="pd-chip pd-chip--protocol">{{pr}}</span>
                    {{/each}}
                  </div>
                </div>
              {{/if}}

              {{#if this.provider.countries.length}}
                <div class="pd-spec-chips-section">
                  <h4 class="pd-detail__subhead">Supported Countries ({{this.provider.countries.length}})</h4>
                  <div class="pd-detail__chips">
                    {{#each this.provider.countries as |c|}}
                      <span class="pd-chip">{{c}}</span>
                    {{/each}}
                  </div>
                </div>
              {{/if}}
            </div>
          {{/if}}

          {{#if (eq this.tab "setup")}}
            <div class="pd-detail__card">
              <h3>Setup guide</h3>
              <p>{{if this.profile.setup_guide this.profile.setup_guide "No setup guide available yet."}}</p>
            </div>
          {{/if}}

          {{#if (eq this.tab "changelog")}}
            <div class="pd-detail__card pd-detail__card--changelog">
              <h3>📈 Provider Update History</h3>
              <div class="pd-changelog-list">
                {{#each @model.changelog as |event|}}
                  <div class="pd-changelog-item">
                    <div class="pd-changelog-item__meta">
                      <span class="pd-changelog-item__date">{{this.formatDate event.occurred_at}}</span>
                      <span class="pd-changelog-item__user">by {{event.triggered_by}}</span>
                    </div>
                    <p class="pd-changelog-item__desc">{{event.description}}</p>
                    {{#if event.old_value}}
                      <div class="pd-changelog-item__values">
                        <span class="old-val">{{event.old_value}}</span>
                        <span class="arrow">→</span>
                        <span class="new-val">{{event.new_value}}</span>
                      </div>
                    {{/if}}
                  </div>
                {{/each}}
              </div>
            </div>
          {{/if}}
        </div>

        <aside class="pd-detail__sidebar">
          <div class="pd-detail__cta-card">
            <div class="pd-detail__cta-price">{{this.provider.costDisplay}}{{#if this.hasIpPricing}}<span>/IP</span>{{else}}<span>/GB</span>{{/if}}</div>

            {{#if this.provider.type_pricing}}
              <div class="pd-sidebar-pricing">
                {{#each-in this.provider.type_pricing as |type data|}}
                  <span class="pd-type-price-tag pd-type-{{type}}">{{type}}: ${{data.price}}{{if (eq data.unit "per_ip") "/IP" (if (eq data.unit "flat") " flat" "/GB")}}</span>
                {{/each-in}}
              </div>
            {{/if}}

            {{#if this.provider.metadata.deal}}
              <div class="pd-detail__promo-card">
                <div class="promo-header">🎁 Exclusive Deal</div>
                <div class="promo-body">{{this.provider.metadata.deal}}</div>
                {{#if this.provider.metadata.coupon_code}}
                  <div class="promo-coupon">
                    <span class="label">Coupon Code:</span>
                    <code>{{this.provider.metadata.coupon_code}}</code>
                  </div>
                {{/if}}
                {{#if this.provider.metadata.expiry}}
                  <div class="promo-expiry">⏳ {{this.provider.metadata.expiry}}</div>
                {{/if}}
              </div>
            {{/if}}

            <a class="pd-detail__cta-btn" href={{this.provider.goUrl}} rel="sponsored nofollow" target="_blank">
              {{this.provider.ctaText}} ↗
            </a>
            <p class="pd-detail__cta-note">Affiliate link — we may earn a commission at no cost to you.</p>
            <a class="pd-detail__cta-secondary" href="/providers/compare">⚖ Compare with another</a>
          </div>

          <div class="pd-detail__quickcard">
            <h4>At a glance</h4>
            <div class="pd-spec pd-spec--full">
              <span>Type</span>
              <div class="pd-detail__types">
                {{#each this.provider.proxy_types as |t|}}
                  <span class="pd-type-pill pd-type-{{t}}">{{t}}</span>
                {{/each}}
              </div>
            </div>
            <div class="pd-spec"><span>Rating</span><strong>★ {{this.provider.ratingDisplay}}</strong></div>
            <div class="pd-spec"><span>Uptime</span><strong>{{formatPercent this.provider.uptime_percentage}}</strong></div>
            <div class="pd-spec"><span>Pool</span><strong>{{formatNumber this.provider.ip_pool_size}}</strong></div>
            {{#if this.provider.metadata.free_trial}}
              <div class="pd-spec"><span>Free Trial</span><strong>{{this.provider.metadata.free_trial}}</strong></div>
            {{/if}}
            {{#if this.provider.metadata.speed}}
              <div class="pd-spec"><span>Tested Speed</span><strong>{{this.provider.metadata.speed}}</strong></div>
            {{/if}}
            {{#if this.provider.metadata.locations}}
              <div class="pd-spec"><span>Locations</span><strong>{{this.provider.metadata.locations}}</strong></div>
            {{/if}}
          </div>

          <a class="pd-detail__back" href="/providers">← Back to directory</a>
        </aside>
      </div>

      {{#if this.showReviewModal}}
        <div class="pd-modal-backdrop" {{on "click" this.closeReviewModal}}>
          <div class="pd-modal-container" {{on "click" this.stopProp}}>
            <div class="pd-modal-header">
              <h3>Write a Review for {{this.provider.name}}</h3>
              <button type="button" class="pd-modal-close-btn" {{on "click" this.closeReviewModal}}>&times;</button>
            </div>
            
            <form class="pd-review-form" {{on "submit" this.submitReview}}>
              {{#if this.submitError}}
                <div class="alert alert-error">{{this.submitError}}</div>
              {{/if}}

              <div class="pd-form-field">
                <label for="review-title">Review Title</label>
                <input type="text" id="review-title" required placeholder="Summarize your experience..." value={{this.reviewTitle}} {{on "input" this.updateTitle}} />
              </div>

              <div class="pd-form-scores-grid">
                <div class="pd-form-field">
                  <label for="score-overall">Overall (1-10)</label>
                  <input type="number" id="score-overall" min="1" max="10" required value={{this.reviewOverall}} {{on "input" this.updateOverall}} />
                </div>
                <div class="pd-form-field">
                  <label for="score-reliability">Reliability (1-10)</label>
                  <input type="number" id="score-reliability" min="1" max="10" required value={{this.reviewReliability}} {{on "input" this.updateReliability}} />
                </div>
                <div class="pd-form-field">
                  <label for="score-speed">Speed (1-10)</label>
                  <input type="number" id="score-speed" min="1" max="10" required value={{this.reviewSpeed}} {{on "input" this.updateSpeed}} />
                </div>
                <div class="pd-form-field">
                  <label for="score-support">Support (1-10)</label>
                  <input type="number" id="score-support" min="1" max="10" required value={{this.reviewSupport}} {{on "input" this.updateSupport}} />
                </div>
                <div class="pd-form-field">
                  <label for="score-value">Value (1-10)</label>
                  <input type="number" id="score-value" min="1" max="10" required value={{this.reviewValue}} {{on "input" this.updateValue}} />
                </div>
              </div>

              <div class="pd-form-field">
                <label for="review-works">👍 What Works Well?</label>
                <textarea id="review-works" required placeholder="What are the strong points..." value={{this.reviewWorks}} {{on "input" this.updateWorks}}></textarea>
              </div>

              <div class="pd-form-field">
                <label for="review-hurts">👎 What Hurts / Can Improve?</label>
                <textarea id="review-hurts" required placeholder="What issues did you face..." value={{this.reviewHurts}} {{on "input" this.updateHurts}}></textarea>
              </div>

              <div class="pd-modal-actions">
                <button type="button" class="btn btn-default" {{on "click" this.closeReviewModal}}>Cancel</button>
                <button type="submit" class="btn btn-primary {{if this.submitting 'disabled'}}" disabled={{this.submitting}}>
                  {{if this.submitting "Submitting..." "Submit Review"}}
                </button>
              </div>
            </form>
          </div>
        </div>
      {{/if}}
      {{#if this.showClaimForm}}
        <div class="pd-modal-overlay" {{on "click" this.closeClaimModal}}>
          <div class="pd-modal pd-modal--claim" {{on "click" this.stopPropagation}}>
            <div class="pd-modal__header">
              <div class="pd-modal__title">
                <span class="pd-modal__icon" aria-hidden="true">🛡️</span>
                <h2>Claim {{this.provider.name}}</h2>
              </div>
              <button type="button" class="pd-modal__close" {{on "click" this.closeClaimModal}}>✕</button>
            </div>
            <div class="pd-modal__body">
              <p class="pd-modal__lead">Verify you represent this company to unlock the Vendor Dashboard and manage your listing.</p>

              <ul class="pd-claim-benefits">
                <li><span class="pd-claim-benefits__check">✓</span> Manage your provider profile &amp; specs</li>
                <li><span class="pd-claim-benefits__check">✓</span> Respond to customer reviews</li>
                <li><span class="pd-claim-benefits__check">✓</span> Access performance analytics</li>
              </ul>

              <div class="pd-form-field">
                <label for="claim-email">Company Email</label>
                <input id="claim-email" type="email" placeholder="you@{{this.provider.name}}.com" value={{this.claimEmail}} {{on "input" (fn this.setProperty "claimEmail")}} />
              </div>

              <div class="pd-form-field">
                <label for="claim-notes">Notes / Proof <span class="pd-form-optional">(optional)</span></label>
                <textarea id="claim-notes" rows="3" placeholder="I am the marketing manager..." value={{this.claimNotes}} {{on "input" (fn this.setProperty "claimNotes")}}></textarea>
              </div>

              <button type="button" class="pd-btn pd-btn--primary pd-modal__submit" {{on "click" this.submitClaim}} disabled={{this.claiming}}>
                {{if this.claiming "Submitting…" "Submit Claim"}}
              </button>
              <p class="pd-modal__footnote">We typically review claims within 2–3 business days.</p>
            </div>
          </div>
        </div>
      {{/if}}
    </section>
  </template>
}
